import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api, apiBlob } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = (incomeId) => ({ item_id: '', description: '', quantity: 1, unit_price: 0, taxable: true, income_account_id: incomeId || '' });

export default function Quotes() {
  const { activeId } = useBusiness();
  const { can } = useAuth();
  const editable = can('quotes.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new'); // new | edit | view
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);

  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/quotes?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load quotes.'); })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api(`/invoices/meta?business_id=${activeId}`).then(setMeta).catch(() => setMeta(null));
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
    api(`/items?business_id=${activeId}&active=1`).then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([]));
  }, [activeId]);

  const taxRate = meta?.collects_sales_tax ? Number(meta.tax_rate) || 0 : 0;
  const defaultIncome = meta?.default_income_account_id || '';

  const totals = useMemo(() => {
    let subtotal = 0, taxable = 0;
    for (const l of lines) {
      const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
      subtotal += amt;
      if (l.taxable !== false) taxable += amt;
    }
    const tax = taxable * taxRate;
    return { subtotal, tax, total: subtotal + tax };
  }, [lines, taxRate]);

  function openNew() {
    setMode('new'); setCurrent(null); setSendMsg(null);
    setForm({ customer_id: '', issue_date: today(), expiry_date: '', terms: '' });
    setLines([emptyLine(defaultIncome)]);
    setFormError(null); setOpen(true);
  }

  async function openRow(row) {
    setFormError(null); setSendMsg(null); setOpen(true);
    try {
      const q = await api(`/quotes/${row.id}`);
      setCurrent(q);
      setMode(q.status === 'converted' ? 'view' : 'edit');
      setForm({
        customer_id: q.customer_id,
        issue_date: q.issue_date?.slice(0, 10) || today(),
        expiry_date: q.expiry_date?.slice(0, 10) || '',
        terms: q.terms || '',
      });
      setLines((q.lines || []).map((l) => ({
        item_id: l.item_id || '', description: l.description, quantity: Number(l.quantity),
        unit_price: Number(l.unit_price), taxable: l.taxable, income_account_id: l.income_account_id || '',
      })));
    } catch { setOpen(false); }
  }

  const readOnly = mode === 'view' || !editable;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine(defaultIncome)]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  function selectItem(i, itemId) {
    const it = items.find((x) => String(x.id) === String(itemId));
    setLines((ls) => ls.map((l, idx) => {
      if (idx !== i) return l;
      if (!it) return { ...l, item_id: '' };
      return { ...l, item_id: it.id, description: it.description || it.name, unit_price: Number(it.unit_price) || 0, taxable: it.taxable !== false, income_account_id: it.income_account_id || defaultIncome };
    }));
  }

  async function save() {
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    const clean = lines.filter((l) => l.description.trim());
    if (clean.length === 0) { setFormError('Add at least one line item.'); return; }
    setSaving(true); setFormError(null);
    const body = {
      business_id: activeId, customer_id: Number(form.customer_id),
      issue_date: form.issue_date, expiry_date: form.expiry_date || null, terms: form.terms || null,
      lines: clean.map((l) => ({
        description: l.description.trim(), quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0,
        taxable: l.taxable !== false, income_account_id: l.income_account_id || defaultIncome, item_id: l.item_id || null,
      })),
    };
    try {
      if (mode === 'edit' && current) await api(`/quotes/${current.id}`, { method: 'PUT', body });
      else await api('/quotes', { method: 'POST', body });
      setOpen(false); loadList();
    } catch (e) { setFormError(e?.detail || 'Could not save the quote.'); }
    finally { setSaving(false); }
  }

  async function setStatus(status) {
    if (!current) return;
    setSaving(true); setFormError(null);
    try {
      const q = await api(`/quotes/${current.id}/status`, { method: 'POST', body: { status } });
      setCurrent(q); loadList();
    } catch (e) { setFormError(e?.detail || 'Could not update status.'); }
    finally { setSaving(false); }
  }

  async function downloadPdf() {
    if (!current) return;
    setPdfBusy(true); setSendMsg(null);
    try {
      const blob = await apiBlob(`/quotes/${current.id}/pdf?download=1`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${current.quote_number || 'quote'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { setSendMsg({ ok: false, text: 'Could not generate the PDF.' }); }
    finally { setPdfBusy(false); }
  }

  async function sendQuote() {
    if (!current) return;
    setSending(true); setSendMsg(null);
    try {
      const r = await api(`/quotes/${current.id}/send`, { method: 'POST' });
      if (r.emailed) {
        setSendMsg({ ok: true, text: `Sent to ${r.to}.` });
        if (r.quote) setCurrent(r.quote);
        loadList();
      } else if (r.reason === 'not_configured') {
        setSendMsg({ ok: false, text: 'Server email isn’t set up — use Download PDF and send it manually.' });
      } else {
        setSendMsg({ ok: false, text: `Couldn’t send (${r.reason})${r.detail ? `: ${r.detail}` : ''}` });
      }
    } catch (e) {
      setSendMsg({ ok: false, text: e?.detail === 'no_recipient' ? 'No email address on file for this customer.' : (e?.detail || 'Could not send the quote.') });
    } finally { setSending(false); }
  }

  async function convert() {
    if (!current) return;
    setSaving(true); setFormError(null);
    try {
      await api(`/quotes/${current.id}/convert`, { method: 'POST' });
      setOpen(false); loadList();
      setNotice(`Quote ${current.quote_number} converted to a draft invoice — open Invoices to review and finalize it.`);
    } catch (e) { setFormError(e?.detail || 'Could not convert the quote.'); }
    finally { setSaving(false); }
  }

  const columns = [
    { key: 'quote_number', header: 'Number' },
    { key: 'customer', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'issue_date', header: 'Issued', render: (r) => date(r.issue_date) },
    { key: 'expiry_date', header: 'Expires', render: (r) => date(r.expiry_date) },
    { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const title = mode === 'new' ? 'New quote' : current ? `Quote ${current.quote_number}` : 'Quote';
  const hasSaved = !!current && mode !== 'new';
  const footer = readOnly ? (
    <>
      <button className="btn" onClick={() => setOpen(false)}>Close</button>
      {hasSaved && <button className="btn" onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy ? 'Preparing…' : 'Download PDF'}</button>}
    </>
  ) : (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
      {hasSaved && <button className="btn" onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy ? 'Preparing…' : 'PDF'}</button>}
      <button className="btn btn--primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create quote'}
      </button>
    </>
  );

  return (
    <>
      <PageHeader title="Quotes" actions={editable && <button className="btn btn--primary" onClick={openNew}>New quote</button>} />
      {notice && <div className="notice">{notice}<button className="notice__x" onClick={() => setNotice(null)}>×</button></div>}
      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow} empty="No quotes yet. Create one to send a customer." />
      </section>

      <Drawer open={open} title={title} width={720} onClose={() => !saving && setOpen(false)} footer={form && footer}>
        {form && (
          <>
            {formError && <div className="form-error">{formError}</div>}

            {/* status + actions for existing quotes */}
            {current && mode !== 'new' && (
              <div className="inv-status-row">
                <StatusBadge status={current.status} />
                {current.status === 'converted' && current.converted_invoice_number && (
                  <span className="hint">→ Invoice {current.converted_invoice_number}</span>
                )}
                {current.sent_at && <span className="hint">· Sent {date(current.sent_at)}{current.last_sent_to ? ` to ${current.last_sent_to}` : ''}</span>}
                {current.email_open && current.email_open.first_clicked_at && (
                  <span className="badge badge--green" title={`Clicked ${current.email_open.click_count}× · last ${date(current.email_open.last_clicked_at)}`}>● Clicked link</span>
                )}
                {current.email_open && (current.email_open.first_opened_at
                  ? <span className="badge badge--slate" title={`Opened ${current.email_open.open_count}× · last ${date(current.email_open.last_opened_at)}`}>● Opened</span>
                  : <span className="hint">· not opened yet</span>)}
              </div>
            )}
            {sendMsg && <div className={sendMsg.ok ? 'form-note' : 'form-error'}>{sendMsg.text}</div>}

            {readOnly ? (
              <div className="field"><label>Customer</label><div className="ro-value">{current?.customer_name}</div></div>
            ) : (
              <div className="field">
                <label>Customer *</label>
                <select value={form.customer_id} onChange={(e) => setField('customer_id', e.target.value)}>
                  <option value="">Select a customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </select>
              </div>
            )}

            <div className="form-grid">
              <div className="field"><label>Issue date</label>
                {readOnly ? <div className="ro-value">{date(form.issue_date)}</div>
                  : <input type="date" value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} />}</div>
              <div className="field"><label>Expiry date</label>
                {readOnly ? <div className="ro-value">{date(form.expiry_date)}</div>
                  : <input type="date" value={form.expiry_date} onChange={(e) => setField('expiry_date', e.target.value)} />}</div>
            </div>

            <h3 className="form-section">Line items</h3>
            <div className="li">
              <div className="li__head">
                <span>Item</span><span>Description</span><span>Qty</span><span>Unit price</span><span>Tax</span><span className="li__amt">Amount</span><span />
              </div>
              {lines.map((l, i) => {
                const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                return (
                  <div className="li__row" key={i}>
                    {readOnly ? (
                      <>
                        <span className="li__item">{(items.find((x) => x.id === l.item_id) || {}).name || '—'}</span>
                        <span className="li__desc">{l.description}</span>
                        <span>{Number(l.quantity)}</span>
                        <span className="num">{money(l.unit_price)}</span>
                        <span>{l.taxable !== false ? 'Yes' : '—'}</span>
                        <span className="num li__amt">{money(amt)}</span><span />
                      </>
                    ) : (
                      <>
                        <select className="li__item-sel" value={l.item_id || ''} onChange={(e) => selectItem(i, e.target.value)}>
                          <option value="">— Custom —</option>
                          {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                        </select>
                        <input value={l.description} placeholder="Description" onChange={(e) => setLine(i, 'description', e.target.value)} />
                        <input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                        <input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, 'unit_price', e.target.value)} />
                        <input type="checkbox" checked={l.taxable !== false} onChange={(e) => setLine(i, 'taxable', e.target.checked)} />
                        <span className="num li__amt">{money(amt)}</span>
                        <button type="button" className="li__rm" onClick={() => removeLine(i)} aria-label="Remove">×</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {!readOnly && <div className="li__addrow"><button type="button" className="btn btn--sm" onClick={addLine}>+ Add line</button></div>}

            <div className="totals">
              <div className="totals__row"><span>Subtotal</span><span className="num">{money(totals.subtotal)}</span></div>
              <div className="totals__row"><span>Tax{taxRate ? ` (${(taxRate * 100).toFixed(2)}%)` : ''}</span><span className="num">{money(totals.tax)}</span></div>
              <div className="totals__row totals__row--grand"><span>Total</span><span className="num">{money(totals.total)}</span></div>
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label>Terms / notes</label>
              {readOnly ? <div className="ro-value">{form.terms || '—'}</div>
                : <textarea rows={2} value={form.terms} onChange={(e) => setField('terms', e.target.value)} />}
            </div>

            {/* status actions */}
            {editable && current && mode === 'edit' && (
              <div className="quote-actions">
                <button className="btn btn--sm btn--primary" onClick={sendQuote} disabled={sending || saving}>
                  {sending ? 'Sending…' : current.sent_at ? 'Resend to customer' : 'Email to customer'}
                </button>
                {current.status !== 'accepted' && (
                  <button className="btn btn--sm" onClick={() => setStatus('accepted')} disabled={saving}>Mark accepted</button>
                )}
                {current.status !== 'declined' && (
                  <button className="btn btn--sm" onClick={() => setStatus('declined')} disabled={saving}>Mark declined</button>
                )}
                {current.status !== 'cancelled' && current.status !== 'converted' && (
                  <button className="btn btn--sm btn--danger" onClick={() => setStatus('cancelled')} disabled={saving}>Cancel quote</button>
                )}
                <button className="btn btn--sm btn--primary" onClick={convert} disabled={saving}>Convert to invoice</button>
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
